import { useState, useEffect, useCallback } from 'react';

export interface AccessibilitySettings {
  fontSize: number; // 0 = normal, 1 = large, 2 = extra large
  highContrast: boolean;
  reducedMotion: boolean;
  dyslexicFont: boolean;
  linkHighlight: boolean;
  bigCursor: boolean;
  textSpacing: boolean;
  saturation: number; // 0 = normal, 1 = low, 2 = grayscale
  focusHighlight: boolean;
  hideImages: boolean;
  readableFont: boolean;
  lineHeight: number; // 0 = normal, 1 = large, 2 = extra large
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 0,
  highContrast: false,
  reducedMotion: false,
  dyslexicFont: false,
  linkHighlight: false,
  bigCursor: false,
  textSpacing: false,
  saturation: 0,
  focusHighlight: false,
  hideImages: false,
  readableFont: false,
  lineHeight: 0,
};

const STORAGE_KEY = 'bena-accessibility-settings';

export const useAccessibility = () => {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });

  const updateSetting = useCallback(<K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    setSettings(defaultSettings);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Sync OS prefers-reduced-motion
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches && !settings.reducedMotion) {
      updateSetting('reducedMotion', true);
    }
    const handler = (e: MediaQueryListEvent) => {
      updateSetting('reducedMotion', e.matches);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // Font size
    const fontSizes = ['100%', '120%', '140%'];
    root.style.fontSize = fontSizes[settings.fontSize] || '100%';

    // High contrast
    body.classList.toggle('a11y-high-contrast', settings.highContrast);

    // Reduced motion
    body.classList.toggle('a11y-reduced-motion', settings.reducedMotion);

    // Dyslexic font
    body.classList.toggle('a11y-dyslexic-font', settings.dyslexicFont);

    // Link highlight
    body.classList.toggle('a11y-link-highlight', settings.linkHighlight);

    // Big cursor
    body.classList.toggle('a11y-big-cursor', settings.bigCursor);

    // Text spacing
    body.classList.toggle('a11y-text-spacing', settings.textSpacing);

    // Saturation
    body.classList.remove('a11y-low-saturation', 'a11y-grayscale');
    if (settings.saturation === 1) body.classList.add('a11y-low-saturation');
    if (settings.saturation === 2) body.classList.add('a11y-grayscale');

    // Focus highlight
    body.classList.toggle('a11y-focus-highlight', settings.focusHighlight);

    // Hide images
    body.classList.toggle('a11y-hide-images', settings.hideImages);

    // Readable font
    body.classList.toggle('a11y-readable-font', settings.readableFont);

    // Line height
    body.classList.remove('a11y-line-height-1', 'a11y-line-height-2');
    if (settings.lineHeight === 1) body.classList.add('a11y-line-height-1');
    if (settings.lineHeight === 2) body.classList.add('a11y-line-height-2');
  }, [settings]);

  return { settings, updateSetting, resetAll };
};
